import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// The chart chords in the Chord Compass's centre, while a chart chord is
// showing: the current one large in the middle, its neighbours small and
// faint either side, the rest out of sight.
//
// It works like the Key Center panel's strip, but the current chord is drawn
// much larger than the rest, so instead of sliding one strip, each chord has
// its own place and size, and stepping moves them together, with the old
// chord shrinking out of the middle and the new one growing into it.
//
// The chords within `reach` of the current one are drawn, most of them out of
// sight, so that a step retargets chords already sliding rather than starting
// new ones: stepping quickly stays smooth, and a step as long as the reach
// (the arrow keys' jump) is plain sliding. Through a step the chords around
// the one it started from stay drawn too. A click further off than the reach
// works the chords between into the places — the new chord still comes in
// from that far off — without drawing them. Where one has not been measured,
// its width is the average of those that have; it is off screen, so near
// enough.
//
// Places come from widths measured once per chord at a reference size, from
// hidden copies. Measuring the drawn chords would catch them mid-transition.
//
// Only a change of current chord animates, for the length of that slide. A
// chord whose symbol changes where it stands — the pending chord following
// the keys — snaps to its new size, so it never seems to grow or shrink for
// no reason. A chord that was not drawn before a step — the new middle after
// a jump, a neighbour coming into view — slides in from where it was in the
// layout before the step, out of sight at a neighbour's size, so it moves
// with the chords that were drawn instead of appearing in place. One that
// replaces the middle outright (the pending chord becoming the chart's own)
// is already in place there, and simply stays.
//
// The chords sit on the inner circle's ground and under the note ring,
// clipped to the circle so they never reach the ring or the slices, and faded
// toward its edge by a mask rather than a colour, so the fade works over
// either mode's gradient.
//
// A middle chord that can be added to the chart (a pending one) is clickable,
// with a faint plus below it that brightens on hover. Everything else lets
// clicks through.
//
// `dimmed` draws the middle chord as faint as its neighbours: the chart chord
// is still the place in the chart, but the keys have left it with nothing
// pending in its place, so it reads the way the chart chords do beside a
// pending one.
//
// The component re-renders only when `stripKey` changes, not on every note the
// app plays — so `onAddCentre` must be a stable function, and whether the
// middle chord can be added (`centreAddable`) has to be part of the key.

export interface StripChord<S> {
  id: string;
  segment: S;
}

interface CompassChartStripProps<S> {
  chords: StripChord<S>[];
  /** Index of the current chord in `chords`. */
  current: number;
  /** How many chords either side of the current one to draw. */
  reach: number;
  /** Font size of the current chord; the rest are NEIGHBOUR_SIZE. */
  centreSize: number;
  ink: string;
  drawSymbol: (segment: S) => React.ReactNode;
  /** Whether clicking the middle chord adds it to the chart. */
  centreAddable: boolean;
  /** Draw the middle chord at the neighbours' opacity. */
  dimmed: boolean;
  /** Adds the middle chord. Stable across renders. */
  onAddCentre: () => void;
  /** Changes whenever anything drawn changes: ids, symbols, sizes, ink. */
  stripKey: string;
}

/** Radius of the inner circle the strip lives in, in the Compass's units. */
const RADIUS = 10.2;
/** Width of the fade at the circle's edge. */
const FADE = 2.5;
const NEIGHBOUR_SIZE = 2.6;
const NEIGHBOUR_OPACITY = 0.4;
/** How far below the middle chord's baseline the add plus sits. */
const PLUS_BELOW = 2.5;
/** Space between two chords' ink. */
const GAP = 0.8;
/** The size the hidden copies are measured at. */
const MEASURE_SIZE = 10;
/** As the Key Center panel's strip. */
const SLIDE_MS = 300;
/**
 * The ink fades between black and white with the mode gradient behind it
 * (App.tsx's centre gradient layers, 0.2s ease-in-out), whether or not the
 * chords are sliding, so it never changes ahead of the ground it contrasts with.
 */
const INK_TRANSITION = 'fill 200ms ease-in-out';
const TRANSITION = `transform ${SLIDE_MS}ms ease-out, font-size ${SLIDE_MS}ms ease-out, opacity ${SLIDE_MS}ms ease-out, ${INK_TRANSITION}`;

function CompassChartStripInner<S>({ chords, current, reach, centreSize, ink, drawSymbol, centreAddable, dimmed, onAddCentre }: CompassChartStripProps<S>) {
  const [hovering, setHovering] = useState(false);
  // The hit area goes when the chord is added, without a mouse-leave, so the
  // hover is dropped here rather than left set for the next pending chord.
  useEffect(() => {
    if (!centreAddable) setHovering(false);
  }, [centreAddable]);
  const measureRef = useRef<SVGGElement>(null);
  const drawnRef = useRef<SVGGElement>(null);
  const [widths, setWidths] = useState<{ [id: string]: number }>({});
  // The current chord and the chords drawn last time; the chord the slide in
  // progress started from, and when that slide ends.
  const lastCurrentRef = useRef<string | null>(null);
  const lastDrawnRef = useRef<string[]>([]);
  const slideFromRef = useRef<string | null>(null);
  const slideUntilRef = useRef(0);

  const currentId = chords[current].id;
  const turning = lastCurrentRef.current !== null && lastCurrentRef.current !== currentId;
  const sliding = turning || Date.now() < slideUntilRef.current;
  let slideFrom: string | null = null;
  if (turning) {
    slideFrom = lastCurrentRef.current;
  } else if (sliding) {
    slideFrom = slideFromRef.current;
  }
  const fromIndex = slideFrom !== null ? chords.findIndex(chord => chord.id === slideFrom) : -1;

  // The chords to draw, by index, left to right.
  const wanted: number[] = [];
  for (let i = current - reach; i <= current + reach; i++) wanted.push(i);
  if (fromIndex !== -1) wanted.push(fromIndex - 1, fromIndex, fromIndex + 1);
  const drawn = wanted
    .filter((i, k) => i >= 0 && i < chords.length && wanted.indexOf(i) === k)
    .sort((a, b) => a - b);
  const drawnIds = drawn.map(i => chords[i].id);

  // Each chord's centre around a given middle chord: that one at 0, the rest
  // worked outward across everything between the outermost chords drawn.
  // Around the current chord it places what is drawn; around the chord a step
  // started from, it says where a chord arriving now would have been then.
  const known = Object.keys(widths).map(id => widths[id]);
  const typical = known.length > 0 ? known.reduce((sum, w) => sum + w, 0) / known.length : 0.6;
  const sizeAround = (middle: number, i: number) => (i === middle ? centreSize : NEIGHBOUR_SIZE);
  const placesAround = (middle: number) => {
    const width = (i: number) => {
      const w = widths[chords[i].id];
      return (w === undefined ? typical : w) * sizeAround(middle, i);
    };
    const lo = Math.min(drawn[0], middle);
    const hi = Math.max(drawn[drawn.length - 1], middle);
    const at: { [index: number]: number } = { [middle]: 0 };
    for (let i = middle + 1; i <= hi; i++) {
      at[i] = at[i - 1] + width(i - 1) / 2 + GAP + width(i) / 2;
    }
    for (let i = middle - 1; i >= lo; i--) {
      at[i] = at[i + 1] - width(i + 1) / 2 - GAP - width(i) / 2;
    }
    return at;
  };
  const x = placesAround(current);
  const size = (i: number) => sizeAround(current, i);
  const opacityAt = (offset: number) => {
    if (offset === 0) return dimmed ? NEIGHBOUR_OPACITY : 1;
    if (offset === 1 || offset === -1) return NEIGHBOUR_OPACITY;
    return 0;
  };
  const rise = (offset: number) => (offset === 0 ? 1.6 : 1.3);
  const centreWidth = (widths[currentId] === undefined ? typical : widths[currentId]) * centreSize;

  useLayoutEffect(() => {
    const previous = lastCurrentRef.current;
    const drawnBefore = lastDrawnRef.current;
    lastCurrentRef.current = currentId;
    lastDrawnRef.current = drawnIds;
    if (previous === null || previous === currentId) return;
    slideFromRef.current = previous;
    slideUntilRef.current = Date.now() + SLIDE_MS;
    const group = drawnRef.current;
    if (group === null) return;
    const arrived = (id: string) => drawnBefore.indexOf(id) === -1;
    const node = (id: string) => group.querySelector<SVGTextElement>(`[data-chord="${id}"]`);
    const timing = { duration: SLIDE_MS, easing: 'ease-out' };
    const before = chords.findIndex(chord => chord.id === previous);
    drawn.forEach(i => {
      const id = chords[i].id;
      if (!arrived(id)) return; // drawn before: its CSS transition moves it
      const text = node(id);
      if (text === null) return;
      if (before === -1) {
        // The middle was replaced outright (a pending chord added to the
        // chart): the new one is already in place, and only new neighbours
        // fade in.
        if (i !== current) text.animate([{ opacity: 0 }, { opacity: opacityAt(i - current) }], timing);
        return;
      }
      // A chord not drawn before the step slides in from where it was then —
      // out of sight, a neighbour's size — like the ones that were drawn.
      const then = placesAround(before);
      text.animate([
        { transform: `translate(${then[i]}px, ${rise(i - before)}px)`, fontSize: `${NEIGHBOUR_SIZE}px`, opacity: 0 },
        { transform: `translate(${x[i]}px, ${rise(i - current)}px)`, fontSize: `${size(i)}px`, opacity: opacityAt(i - current) },
      ], timing);
    });
  });

  useLayoutEffect(() => {
    const group = measureRef.current;
    if (group === null) return;
    const measured: { [id: string]: number } = Object.assign({}, widths);
    let changed = false;
    group.querySelectorAll<SVGTextElement>('[data-measure]').forEach(text => {
      const id = text.getAttribute('data-measure') as string;
      const w = text.getBBox().width / MEASURE_SIZE;
      if (widths[id] === undefined || Math.abs(widths[id] - w) > 0.001) changed = true;
      measured[id] = w;
    });
    if (changed) setWidths(measured);
  });

  const fadeAt = FADE / (2 * RADIUS);

  return (
    <g ref={drawnRef} clipPath="url(#compassStripClip)" mask="url(#compassStripFade)" style={{ pointerEvents: 'none' }}>
      <defs>
        <clipPath id="compassStripClip">
          <circle cx={0} cy={0} r={RADIUS} />
        </clipPath>
        <linearGradient id="compassStripGradient" gradientUnits="userSpaceOnUse" x1={-RADIUS} y1={0} x2={RADIUS} y2={0}>
          <stop offset={0} stopColor="black" />
          <stop offset={fadeAt} stopColor="white" />
          <stop offset={1 - fadeAt} stopColor="white" />
          <stop offset={1} stopColor="black" />
        </linearGradient>
        <mask id="compassStripFade" maskUnits="userSpaceOnUse" x={-RADIUS} y={-RADIUS} width={2 * RADIUS} height={2 * RADIUS}>
          <rect x={-RADIUS} y={-RADIUS} width={2 * RADIUS} height={2 * RADIUS} fill="url(#compassStripGradient)" />
        </mask>
      </defs>
      {drawn.map(i => {
        const chord = chords[i];
        const offset = i - current;
        const opacity = opacityAt(offset);
        return (
          <text
            key={chord.id}
            data-chord={chord.id}
            className="pie-chord-symbol"
            x={0}
            y={0}
            style={{
              fill: ink,
              fontSize: `${size(i)}px`,
              transform: `translate(${x[i]}px, ${rise(offset)}px)`,
              opacity: opacity,
              transition: sliding ? TRANSITION : INK_TRANSITION,
            }}
          >
            {drawSymbol(chord.segment)}
          </text>
        );
      })}
      {/* The middle chord's hit area: its whole box rather than its glyphs,
          so the gaps between them do not flicker the hover. */}
      {centreAddable && (
        <rect
          data-actionable="true"
          x={-centreWidth / 2}
          y={1.6 - centreSize * 0.85}
          width={centreWidth}
          height={centreSize * 0.85 + PLUS_BELOW + 0.4}
          fill="transparent"
          style={{ pointerEvents: 'all', cursor: 'pointer' }}
          onClick={onAddCentre}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
        >
          <title>Add to the chart (Return)</title>
        </rect>
      )}
      {/* The plus sits below the chord, never above, where it would read
          as the + of an augmented chord. Faint whenever the chord can be
          added, full on hover. */}
      {centreAddable && (
        <text
          className="pie-chord-symbol"
          x={0}
          y={1.6 + PLUS_BELOW}
          opacity={hovering ? 1 : 0.3}
          style={{ fill: ink, transition: INK_TRANSITION, fontFamily: 'Trebuchet MS, sans-serif', fontWeight: 'bold', fontSize: '2.4px', textAnchor: 'middle', pointerEvents: 'none' }}
        >
          {'+'}
        </text>
      )}
      <g ref={measureRef} style={{ visibility: 'hidden' }}>
        {drawn.map(i => chords[i]).map(chord => (
          <text key={chord.id} data-measure={chord.id} className="pie-chord-symbol" x={0} y={0} style={{ fontSize: `${MEASURE_SIZE}px` }}>
            {drawSymbol(chord.segment)}
          </text>
        ))}
      </g>
    </g>
  );
}

const CompassChartStrip = React.memo(
  CompassChartStripInner,
  (prev, next) => prev.stripKey === next.stripKey
) as typeof CompassChartStripInner;

export default CompassChartStrip;
